import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Play, Pause, Square, RefreshCw, Check, X, Rocket, AlertTriangle,
  ChevronRight, Eye, Pencil, Save, ExternalLink, CheckCircle2, XCircle, Clock, Sparkles, Maximize2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API_BASE_URL from '../Config/config';
import { TOPIC_STATUS_TONES, contentWorkspacePath } from './content/eliteContentStatus';

/**
 * EliteGenerationMonitor — Phase C (25-Aug-2026): everything after
 * "Approve Roadmap" (spec §10-§16).
 *
 *   Start Content Generation → (workers run on the server, this polls
 *   GET .../content/status every 5s) → per-topic stage progress, cost, ETA,
 *   pause / resume / cancel / retry-failed → review each topic (preview,
 *   edit, approve, regenerate) → Approve all → Publish (git + S3 + CloudFront)
 *
 * Everything shown is persisted server-side (elite_content_runs /
 * elite_topic_content); reloading the page resumes exactly where it was.
 */

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId || user?.user_id || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};
const authHeaders = () => ({ 'Content-Type': 'application/json', 'user-id': getUserId() || '' });
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders(), ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.data = data; throw err;
  }
  return data;
};

const STAGE_LABELS = {
  research: 'Research', teaching: 'Teaching', production: 'Production', interview: 'Interview',
  hands_on: 'Hands-on', mastery: 'Mastery', review: 'Review', assemble: 'Assemble',
};
// Shared with the full-screen Content Workspace (content/eliteContentStatus.js)
const TOPIC_TONES = TOPIC_STATUS_TONES;
const RUN_TONES = { RUNNING: 'bg-blue-50 text-blue-700', PAUSED: 'bg-amber-50 text-amber-700', COMPLETED: 'bg-emerald-50 text-emerald-700', FAILED: 'bg-red-50 text-red-600', CANCELLED: 'bg-gray-100 text-gray-500', QUEUED: 'bg-gray-100 text-gray-600' };

const Pill = ({ tone, children }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${tone}`}>{children}</span>
);
const fmtMin = (m) => (m == null ? '—' : m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`);
const fmtElapsed = (from, to) => {
  if (!from) return '—';
  const s = Math.max(0, Math.floor(((to ? new Date(to) : new Date()) - new Date(from)) / 1000));
  return `${Math.floor(s / 3600) ? `${Math.floor(s / 3600)}h ` : ''}${Math.floor((s % 3600) / 60)}m ${s % 60}s`;
};

const EliteGenerationMonitor = ({ slug, onBack, onTechnologyChange }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicContent, setTopicContent] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [publishResult, setPublishResult] = useState(null);
  const [publishOpts, setPublishOpts] = useState({ includeNeedsReview: false, excludeFailed: false });
  const [tick, setTick] = useState(0);
  const onChangeRef = useRef(onTechnologyChange);
  useEffect(() => { onChangeRef.current = onTechnologyChange; }, [onTechnologyChange]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await api(`/api/admin/elite-technologies/${slug}/content/status`);
      setStatus(data);
      setError(null);
      if (onChangeRef.current && data.technology) onChangeRef.current(data.technology);
    } catch (err) {
      setError(err.message || 'Failed to load generation status');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const run = status?.run || null;
  const tech = status?.technology || null;
  const isRunning = run?.status === 'RUNNING' || run?.status === 'QUEUED';

  // Poll while a run is active (survives reloads — the run state is server-side).
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => load({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [isRunning, load]);
  useEffect(() => { if (!isRunning) return undefined; const id = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(id); }, [isRunning]);

  const act = async (fn, { reload = true } = {}) => {
    setBusy(true);
    try { const out = await fn(); if (reload) await load({ silent: true }); return out; }
    catch (err) { window.alert(err.message || 'Action failed'); return null; }
    finally { setBusy(false); }
  };
  const post = (p, body) => api(`/api/admin/elite-technologies/${slug}/content/${p}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

  const handleStart = (force = false) => {
    if (force && !window.confirm('Regenerate ALL topics from scratch? Existing generated content for this technology will be replaced.')) return;
    act(() => post('generate', { force }));
  };
  const handlePause = () => act(() => post('pause'));
  const handleResume = () => act(() => post('resume'));
  const handleCancel = () => { if (window.confirm('Cancel this generation run? Completed topics are kept; the technology goes back to ROADMAP APPROVED.')) act(() => post('cancel')); };
  const handleRetryFailed = () => act(() => post('retry-failed'));
  const handleApproveAll = () => act(() => post('approve'));
  const handlePublish = async () => {
    if (!window.confirm('Publish this technology\'s content? It will be committed to the repo, uploaded to the site, and become visible to learners.')) return;
    const out = await act(() => post('publish', publishOpts));
    if (out) setPublishResult(out);
  };

  const openTopic = async (topicId) => {
    setSelectedTopicId(topicId); setEditing(false); setTopicContent(null); setTopicLoading(true);
    try { const data = await api(`/api/admin/elite-technologies/${slug}/content/topics/${topicId}`); setTopicContent(data.content); }
    catch (err) { setTopicContent({ error: err.message }); }
    finally { setTopicLoading(false); }
  };
  const handleApproveTopic = () => act(async () => { await post(`topics/${selectedTopicId}/approve`); await openTopic(selectedTopicId); });
  const handleRegenerateTopic = () => { if (window.confirm('Regenerate this topic from scratch?')) act(async () => { await post(`topics/${selectedTopicId}/regenerate`); setSelectedTopicId(null); }); };
  const handleSaveEdit = () => act(async () => {
    await api(`/api/admin/elite-technologies/${slug}/content/topics/${selectedTopicId}`, { method: 'PUT', body: JSON.stringify({ markdown: draft }) });
    setEditing(false); await openTopic(selectedTopicId);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] p-6 flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-violet-500" />
      </div>
    );
  }

  const counts = status?.counts || {};
  const totals = status?.totals || { topics: 0, done: 0, stagesDone: 0, stagesTotal: 0 };
  const genStatus = tech?.generation_status;
  const canStart = ['ROADMAP_APPROVED', 'FAILED', 'CONTENT_PENDING', 'OUTDATED'].includes(genStatus) && !isRunning;
  const hasContent = totals.done > 0;
  const canPublish = !isRunning && hasContent && ['GENERATED', 'IN_REVIEW', 'APPROVED', 'PUBLISHED'].includes(genStatus);
  const stagePct = totals.stagesTotal ? Math.round((totals.stagesDone / totals.stagesTotal) * 100) : 0;
  const selected = selectedTopicId && (status?.sections || []).flatMap((s) => s.topics).find((t) => t.id === selectedTopicId);

  return (
    <div className="min-h-screen bg-[#F6F7FB] p-6">
      <div className="max-w-6xl mx-auto pb-16">
        <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800">
          <ArrowLeft size={16} /> Back to Technology Intelligence
        </button>

        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-gray-900">{tech?.title} — Content Factory</h1>
            <p className="text-xs text-gray-500 mt-1">
              Technology status: <span className="font-bold">{(genStatus || '').replace(/_/g, ' ')}</span>
              {run && <> · Run #{run.id} <Pill tone={RUN_TONES[run.status] || RUN_TONES.QUEUED}>{run.status}</Pill></>}
              {busy && <Loader2 size={12} className="inline animate-spin text-violet-500 ml-2" />}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canStart && (
              <button onClick={() => handleStart(false)} disabled={busy} className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}>
                <Sparkles size={14} /> {hasContent ? 'Generate missing / changed topics' : 'Start Content Generation'}
              </button>
            )}
            {!isRunning && hasContent && genStatus !== 'ROADMAP_APPROVED' && (
              <button onClick={() => handleStart(true)} disabled={busy} className="px-3 py-2 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-40">
                <RefreshCw size={13} /> Regenerate all
              </button>
            )}
            {isRunning && (
              <>
                <button onClick={handlePause} disabled={busy} className="px-3 py-2 rounded-lg text-xs font-bold border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1.5 disabled:opacity-40"><Pause size={13} /> Pause</button>
                <button onClick={handleCancel} disabled={busy} className="px-3 py-2 rounded-lg text-xs font-bold border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-40"><Square size={13} /> Cancel</button>
              </>
            )}
            {run?.status === 'PAUSED' && (
              <>
                <button onClick={handleResume} disabled={busy} className="px-3 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: 'linear-gradient(135deg,#059669,#10B981)' }}><Play size={13} /> Resume</button>
                <button onClick={handleCancel} disabled={busy} className="px-3 py-2 rounded-lg text-xs font-bold border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-40"><Square size={13} /> Cancel</button>
              </>
            )}
            {!isRunning && counts.FAILED > 0 && (
              <button onClick={handleRetryFailed} disabled={busy} className="px-3 py-2 rounded-lg text-xs font-bold border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-40"><RefreshCw size={13} /> Retry {counts.FAILED} failed</button>
            )}
          </div>
        </div>

        {error && <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-4">{error}</div>}
        {run?.status === 'PAUSED' && run.error && (
          <div className="p-3 bg-amber-50 border-l-4 border-amber-500 text-amber-800 text-xs rounded mb-4"><AlertTriangle size={12} className="inline mr-1" />Paused automatically: {run.error}</div>
        )}

        {/* Progress */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide">Generation Monitor</h2>
            <div className="text-[11px] text-gray-500 flex gap-3 flex-wrap">
              <span>{totals.done}/{totals.topics} topics</span>
              <span>{totals.stagesDone}/{totals.stagesTotal} stages</span>
              {isRunning && <span data-tick={tick}><Clock size={11} className="inline" /> {fmtElapsed(run?.started_at)} elapsed{status?.etaMinutes != null && <> · ~{fmtMin(status.etaMinutes)} left</>}</span>}
              {run?.finished_at && !isRunning && <span>took {fmtElapsed(run.started_at, run.finished_at)}</span>}
              {status?.activeWorkers != null && isRunning && <span>{status.activeWorkers} worker(s) active</span>}
              <span>est. cost so far: <b>${(status?.cost?.estimatedUsd ?? 0).toFixed(2)}</b> ({status?.cost?.calls || 0} AI calls)</span>
            </div>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-3 rounded-full transition-all" style={{ width: `${stagePct}%`, background: 'linear-gradient(90deg,#5B21B6,#7C3AED)' }} />
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {['PENDING', 'IN_PROGRESS', 'GENERATED', 'NEEDS_REVIEW', 'APPROVED', 'PUBLISHED', 'FAILED'].map((k) => (
              <Pill key={k} tone={TOPIC_TONES[k]}>{k.replace('_', ' ')}: {counts[k] || 0}</Pill>
            ))}
            {counts.NONE > 0 && <Pill tone={TOPIC_TONES.NONE}>NOT STARTED: {counts.NONE}</Pill>}
          </div>
          {!run && (
            <p className="text-xs text-gray-500 mt-3">
              Nothing generated yet. Starting generation creates one Elite document per roadmap topic ({totals.topics} topics × {status?.stages?.length || 8} stages), running on the server in parallel — it keeps going if you leave this page.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Topic list */}
          <div className="lg:col-span-2 space-y-3">
            {(status?.sections || []).map((section) => (
              <div key={section.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <h3 className="font-black text-gray-800 text-xs mb-2">{section.title}</h3>
                <div className="space-y-1">
                  {section.topics.map((t) => {
                    const c = t.content;
                    const st = c ? c.status : 'NONE';
                    const active = selectedTopicId === t.id;
                    const openable = !!c && c.status !== 'PENDING';
                    return (
                      <div key={t.id} className="flex items-stretch gap-1">
                      <button
                        onClick={() => (openable ? openTopic(t.id) : null)}
                        className={`flex-1 min-w-0 text-left px-2 py-1.5 rounded-lg border text-xs flex items-center gap-2 ${active ? 'border-violet-300 bg-violet-50' : 'border-gray-100 hover:bg-gray-50'}`}
                        title={c?.error || ''}
                      >
                        {st === 'IN_PROGRESS' ? <Loader2 size={12} className="animate-spin text-blue-500 shrink-0" />
                          : st === 'FAILED' ? <XCircle size={12} className="text-red-500 shrink-0" />
                          : ['GENERATED', 'APPROVED', 'PUBLISHED'].includes(st) ? <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                          : st === 'NEEDS_REVIEW' ? <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                          : <ChevronRight size={12} className="text-gray-300 shrink-0" />}
                        <span className="flex-1 truncate font-semibold text-gray-800">{t.title}</span>
                        {c && st === 'IN_PROGRESS' && <span className="text-[10px] text-blue-600">{STAGE_LABELS[c.current_stage] || c.current_stage} · {c.stages_done}/{c.stages_total}</span>}
                        {c && c.quality_score != null && <span className={`text-[10px] font-bold ${c.quality_score >= (status?.qualityMinScore || 70) ? 'text-emerald-600' : 'text-amber-600'}`}>{c.quality_score}</span>}
                        <Pill tone={TOPIC_TONES[st]}>{st.replace('_', ' ')}</Pill>
                      </button>
                      {openable && c.status !== 'IN_PROGRESS' && (
                        <button
                          type="button"
                          onClick={() => navigate(contentWorkspacePath(slug, t.id))}
                          className="shrink-0 px-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-violet-700 hover:bg-violet-50 hover:border-violet-200"
                          title="Open full preview"
                          aria-label={`Open full preview of ${t.title}`}
                        >
                          <Maximize2 size={12} />
                        </button>
                      )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Preview / review pane */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 min-h-[300px]">
              {!selectedTopicId ? (
                <div className="text-sm text-gray-400 text-center py-16">
                  <Eye size={22} className="mx-auto mb-2 text-gray-300" />
                  Select a generated topic to preview, edit, approve or regenerate it.
                </div>
              ) : topicLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 size={22} className="animate-spin text-violet-500" /></div>
              ) : topicContent?.error ? (
                <div className="text-xs text-red-600">{topicContent.error}</div>
              ) : topicContent ? (
                <>
                  <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                    <div>
                      <h3 className="font-black text-gray-900 text-sm">{topicContent.title}</h3>
                      <p className="text-[11px] text-gray-500">
                        {topicContent.section_title} · <Pill tone={TOPIC_TONES[topicContent.status]}>{topicContent.status.replace('_', ' ')}</Pill>
                        {topicContent.quality_score != null && <> · quality <b>{topicContent.quality_score}</b>/100</>}
                        {topicContent.word_count != null && <> · {topicContent.word_count.toLocaleString()} words</>}
                        {topicContent.published_path && <> · published at <code>/docs/{topicContent.published_path}</code></>}
                      </p>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {!editing && topicContent.status !== 'IN_PROGRESS' && (
                        <button
                          type="button"
                          onClick={() => navigate(contentWorkspacePath(slug, selectedTopicId))}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-white inline-flex items-center gap-1 shadow-sm"
                          style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}
                          title="Open this topic in the full-screen preview & editor"
                        >
                          <Maximize2 size={12} /> Open Full Preview
                        </button>
                      )}
                      {!editing && topicContent.content_markdown && ['GENERATED', 'NEEDS_REVIEW', 'APPROVED', 'PUBLISHED', 'FAILED'].includes(topicContent.status) && (
                        <button onClick={() => { setDraft(topicContent.content_markdown); setEditing(true); }} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-40"><Pencil size={12} /> Edit</button>
                      )}
                      {editing && (
                        <>
                          <button onClick={handleSaveEdit} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-white inline-flex items-center gap-1 disabled:opacity-40" style={{ background: '#059669' }}><Save size={12} /> Save as approved</button>
                          <button onClick={() => setEditing(false)} className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-500 inline-flex items-center gap-1"><X size={12} /> Cancel</button>
                        </>
                      )}
                      {!editing && ['GENERATED', 'NEEDS_REVIEW'].includes(topicContent.status) && (
                        <button onClick={handleApproveTopic} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-white inline-flex items-center gap-1 disabled:opacity-40" style={{ background: '#059669' }}><Check size={12} /> Approve</button>
                      )}
                      {!editing && !isRunning && (
                        <button onClick={handleRegenerateTopic} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-violet-200 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1 disabled:opacity-40"><RefreshCw size={12} /> Regenerate</button>
                      )}
                    </div>
                  </div>

                  {topicContent.error && <div className="p-2 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-3">{topicContent.error}</div>}

                  {/* Stage checkpoints */}
                  <div className="flex gap-1 flex-wrap mb-3">
                    {(status?.stages || []).map((s) => {
                      const st = topicContent.stages?.[s]?.status;
                      const tone = st === 'done' ? 'bg-emerald-50 text-emerald-700' : st === 'running' ? 'bg-blue-50 text-blue-700' : st === 'error' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-400';
                      return <span key={s} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tone}`} title={topicContent.stages?.[s]?.error || ''}>{STAGE_LABELS[s]}{topicContent.stages?.[s]?.attempts > 1 ? ` ×${topicContent.stages[s].attempts}` : ''}</span>;
                    })}
                  </div>

                  {/* Review notes */}
                  {topicContent.review_notes && (topicContent.review_notes.issues || []).length > 0 && (
                    <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                      <p className="text-[11px] font-bold text-amber-800 mb-1">AI review — {topicContent.review_notes.summary}</p>
                      <ul className="text-[11px] text-amber-900 space-y-0.5">
                        {topicContent.review_notes.issues.map((i, idx) => (
                          <li key={idx}><b className="uppercase">{i.severity}</b> · §{i.section}: {i.issue}{i.suggested_fix ? <span className="text-amber-700"> — fix: {i.suggested_fix}</span> : null}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {topicContent.quality_checks && !topicContent.quality_checks.pass && (
                    <div className="mb-3 text-[11px] text-amber-700">
                      Structural checks: {topicContent.quality_checks.headings} sections{topicContent.quality_checks.hasAllSections ? '' : ' (expected ≥26)'}, {topicContent.quality_checks.codeBlocks} code blocks{topicContent.quality_checks.enoughCode ? '' : ' (expected ≥6)'}{topicContent.quality_checks.hasKnowledgeCheck ? '' : ', Knowledge Check missing'}.
                    </div>
                  )}

                  {editing ? (
                    <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full h-[60vh] font-mono text-xs p-3 border border-gray-200 rounded-lg" spellCheck={false} />
                  ) : topicContent.content_markdown ? (
                    <div className="prose prose-sm max-w-none max-h-[65vh] overflow-y-auto border border-gray-100 rounded-lg p-4 bg-gray-50/40">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{topicContent.content_markdown}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">No content assembled yet{selected?.content?.current_stage ? ` — currently at ${STAGE_LABELS[selected.content.current_stage]}` : ''}.</div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Review & Publish */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mt-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-xs font-black text-gray-400 uppercase tracking-wide mb-1">Review & Publish</h2>
              <p className="text-xs text-gray-500">
                Publishing writes <code>public/docs/{tech?.docs_folder || slug}/…</code> + <code>mapping.json</code>: a commit to the repo (so it survives deploys), a direct upload to the site, and a CloudFront invalidation. The technology becomes <b>active</b> for learners.
              </p>
              <div className="flex gap-4 mt-2 text-[11px] text-gray-600">
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={publishOpts.includeNeedsReview} onChange={(e) => setPublishOpts((p) => ({ ...p, includeNeedsReview: e.target.checked }))} /> include NEEDS REVIEW topics as-is</label>
                <label className="inline-flex items-center gap-1"><input type="checkbox" checked={publishOpts.excludeFailed} onChange={(e) => setPublishOpts((p) => ({ ...p, excludeFailed: e.target.checked }))} /> publish without FAILED topics</label>
              </div>
            </div>
            <div className="flex gap-2">
              {!isRunning && (counts.GENERATED > 0 || counts.NEEDS_REVIEW > 0) && (
                <button onClick={handleApproveAll} disabled={busy} className="px-3 py-2 rounded-lg text-xs font-bold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5 disabled:opacity-40"><Check size={13} /> Approve all generated</button>
              )}
              <button onClick={handlePublish} disabled={!canPublish || busy} title={canPublish ? '' : 'Generate and review content first'} className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: 'linear-gradient(135deg,#059669,#10B981)' }}>
                <Rocket size={14} /> {genStatus === 'PUBLISHED' ? 'Re-publish' : 'Publish'}
              </button>
            </div>
          </div>

          {(publishResult || status?.lastPublication) && (() => {
            const p = publishResult || null;
            const lp = status?.lastPublication;
            const git = p ? p.git : lp?.git_result; const s3 = p ? p.s3 : lp?.s3_result; const cf = p ? p.cloudfront : lp?.cloudfront_result;
            const when = p ? 'just now' : lp?.published_at ? new Date(lp.published_at).toLocaleString() : '';
            const Row = ({ ok, label, detail }) => (
              <div className="flex items-center gap-2 text-[11px]">{ok ? <CheckCircle2 size={12} className="text-emerald-500" /> : <XCircle size={12} className="text-red-500" />}<span className="font-bold text-gray-700">{label}</span><span className="text-gray-500 truncate">{detail}</span></div>
            );
            return (
              <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-100 space-y-1">
                <p className="text-[11px] font-bold text-gray-600">Last publish — {when} · {(p ? p.published : lp?.published_topics) || 0} topic(s){p?.excluded?.length ? ` · ${p.excluded.length} excluded` : ''}</p>
                <Row ok={!!git?.ok} label="Git commit" detail={git?.ok ? <a className="text-violet-600 hover:underline inline-flex items-center gap-0.5" href={git.url} target="_blank" rel="noreferrer">{git.commitSha?.slice(0, 7)} <ExternalLink size={10} /></a> : (git?.error || git?.reason || 'not attempted')} />
                <Row ok={!!s3?.ok} label="Site upload" detail={s3?.ok ? `${s3.uploaded} files → s3://${s3.bucket}/${s3.prefix}` : (s3?.error || s3?.reason || 'not attempted')} />
                <Row ok={!!cf?.ok} label="CloudFront" detail={cf?.ok ? `invalidated ${cf.path} (${cf.invalidationId})` : (cf?.error || cf?.reason || 'not attempted')} />
                {genStatus === 'PUBLISHED' && <p className="text-[11px] text-gray-500 mt-1">Live for learners at <code>/elite-mastery/{slug}</code>{git?.ok ? ' — the repo commit will also trigger the docs deploy job.' : ' — note: the git commit failed, so the next full frontend deploy would remove these files until they are committed.'}</p>}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default EliteGenerationMonitor;
